import { useEffect, useState } from 'react';
import { resolvePromoDeadline } from './promoCountdown';

interface PromoTimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ZERO: PromoTimeLeft = {
  days: 0, hours: 0, minutes: 0, seconds: 0,
};

function splitRemaining(ms: number): PromoTimeLeft {
  if (ms <= 0) {
    return ZERO;
  }
  const totalSeconds = Math.floor(ms / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/**
 * Drives the per-account promotional countdown.
 * Ticks every second and auto-rolls to the next 14-day cycle when it reaches zero.
 * Returns null when the accountId is invalid (caller should not render).
 */
export function usePromoCountdown(accountId: number): PromoTimeLeft | null {
  const [deadline, setDeadline] = useState<number>(() => resolvePromoDeadline(accountId));
  const [timeLeft, setTimeLeft] = useState<PromoTimeLeft>(
    () => splitRemaining(deadline - Date.now()),
  );

  // Re-resolve when the account changes (e.g. accountId becomes available after mount).
  useEffect(() => {
    setDeadline(resolvePromoDeadline(accountId));
  }, [accountId]);

  useEffect(() => {
    if (!deadline) {
      return undefined;
    }

    const tick = () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        // Cycle ended -> roll to the next deadline and keep ticking.
        setDeadline(resolvePromoDeadline(accountId));
      } else {
        setTimeLeft(splitRemaining(remaining));
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadline, accountId]);

  return deadline ? timeLeft : null;
}
